import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="bg-[hsl(20,17%,12%)] text-[hsl(38,44%,96%)]">
      <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <h3 className="font-[family-name:var(--font-display)] text-2xl mb-4">Little Italy Ristorante</h3>
            <p className="text-sm text-[hsl(38,44%,96%)]/70 leading-relaxed">
              Authentische italienische Küche<br />mit Liebe zubereitet in Eberbach.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-[hsl(42,72%,44%)]">Navigation</h4>
            <div className="space-y-2">
              {[
                { to: '/menu', label: t('nav_menu') },
                { to: '/reservation', label: t('nav_reservation') },
                { to: '/order', label: t('nav_order') },
                { to: '/reviews', label: t('nav_reviews') },
                { to: '/about', label: t('nav_about') },
              ].map(link => (
                <Link key={link.to} to={link.to} className="block text-sm text-[hsl(38,44%,96%)]/70 hover:text-[hsl(42,72%,44%)] transition-colors duration-150">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-[hsl(42,72%,44%)]">{t('contact_us')}</h4>
            <div className="space-y-3 text-sm text-[hsl(38,44%,96%)]/70">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-[hsl(42,72%,44%)]" />
                <span>Alter Markt 6<br />69412 Eberbach</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[hsl(42,72%,44%)]" />
                <a href="tel:01797007958" className="hover:text-[hsl(42,72%,44%)] transition-colors duration-150">0179/7007958</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[hsl(42,72%,44%)]" />
                <span>info@little-italy-ristorante.de</span>
              </div>
              <a href="https://wa.me/4901797007958" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#25D366] transition-colors duration-150">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[hsl(42,72%,44%)]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-[hsl(42,72%,44%)]">{t('opening_hours')}</h4>
            <div className="space-y-1.5 text-sm text-[hsl(38,44%,96%)]/70" data-testid="opening-hours">
              <div className="flex justify-between"><span>{t('monday')}</span><span className="text-[hsl(0,72%,45%)]">{t('closed')}</span></div>
              <div className="flex justify-between"><span>{t('tuesday')}-{t('saturday')}</span><span>11:30-14:00</span></div>
              <div className="flex justify-between"><span></span><span>17:00-22:00</span></div>
              <div className="flex justify-between"><span>{t('sunday')}</span><span>11:30-22:00</span></div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-[hsl(38,44%,96%)]/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[hsl(38,44%,96%)]/50">
            © {new Date().getFullYear()} Little Italy Ristorante. {t('all_rights')}.
          </p>
          <div className="flex items-center gap-4 text-xs text-[hsl(38,44%,96%)]/50">
            <Link to="/impressum" className="hover:text-[hsl(42,72%,44%)] transition-colors duration-150">{t('impressum')}</Link>
            <Link to="/privacy" className="hover:text-[hsl(42,72%,44%)] transition-colors duration-150">{t('privacy')}</Link>
            <Link to="/admin" className="hover:text-[hsl(42,72%,44%)] transition-colors duration-150">{t('nav_admin')}</Link>
          </div>
          <div className="w-full sm:w-auto text-center sm:text-right mt-3 sm:mt-0">
            <a href="https://www.time-picture.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[hsl(38,44%,96%)]/40 hover:text-[hsl(42,72%,44%)] transition-colors duration-150">
              Made with time picture
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
